import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const EnvironmentalImpactChart = ({ data }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-4 shadow-conservation-lg">
          <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 mb-1">
              <span className="text-xs text-muted-foreground">{entry?.name}:</span>
              <span className="text-xs font-medium" style={{ color: entry?.color }}>
                {entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg p-6 shadow-conservation border border-border">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-1">Environmental Impact Correlation</h3>
          <p className="text-sm text-muted-foreground">Visitor numbers vs ecosystem health indicators</p>
        </div>
        <Icon name="Leaf" size={24} className="text-primary" />
      </div>
      <div className="w-full h-80" aria-label="Environmental Impact Correlation Line Chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f3a39" />
            <XAxis 
              dataKey="month" 
              stroke="#b0bec5"
              tick={{ fill: '#b0bec5', fontSize: 12 }}
            />
            <YAxis 
              yAxisId="left"
              stroke="#b0bec5"
              tick={{ fill: '#b0bec5', fontSize: 12 }}
              label={{ value: 'Visitors', angle: -90, position: 'insideLeft', fill: '#b0bec5' }}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="#b0bec5"
              tick={{ fill: '#b0bec5', fontSize: 12 }}
              label={{ value: 'Health Index', angle: 90, position: 'insideRight', fill: '#b0bec5' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="visitors" 
              stroke="#ffc107" 
              strokeWidth={3}
              dot={{ fill: '#ffc107', r: 4 }}
              activeDot={{ r: 6 }}
              name="Total Visitors"
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="ecosystemHealth" 
              stroke="#1b8a5b" 
              strokeWidth={3}
              dot={{ fill: '#1b8a5b', r: 4 }}
              activeDot={{ r: 6 }}
              name="Ecosystem Health"
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="biodiversityIndex" 
              stroke="#4a90a4" 
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: '#4a90a4', r: 3 }}
              name="Biodiversity Index"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Users" size={18} className="text-accent" />
            <span className="text-xs text-muted-foreground">Avg Monthly Visitors</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {(data?.reduce((sum, item) => sum + item?.visitors, 0) / data?.length)?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Leaf" size={18} className="text-secondary" />
            <span className="text-xs text-muted-foreground">Avg Ecosystem Health</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {(data?.reduce((sum, item) => sum + item?.ecosystemHealth, 0) / data?.length)?.toFixed(1)}%
          </p>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Activity" size={18} className="text-info" />
            <span className="text-xs text-muted-foreground">Correlation Index</span>
          </div>
          <p className="text-2xl font-bold text-foreground">-0.72</p>
          <p className="text-xs text-muted-foreground mt-1">Moderate negative correlation</p>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalImpactChart;